import React, {useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { removePractice } from '../services/practices';
import LibraryDialog from './shared/libraryDialog';
import DateDialog from './shared/dateTimePicker';
import { practicesList } from '../services/practices';

import * as dayjs from 'dayjs';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';

export const PracticeCalendar = ({filters}) => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [dateState, setDateState] = useState(new Date());
  const [dayPractices, setDayPractices] = useState({});
  const [selectedPractices, setSelectedPractices] = useState([]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [practiceToRemove, setPracticeToRemove] = useState(null);

  const [isLibraryDialogOpen, setIsLibraryDialogOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [refreshPractices, setRefreshPractices] = useState(false);
  const [method, setMethod] = useState('');
  const [practice, setPractice] = useState({});

  const [startDate, setStartDate] = useState(dayjs(dateState).startOf('month').subtract(7, 'day'));
  const [endDate, setEndDate] = useState(dayjs(dateState).endOf('month').add(7, 'day'));

  const { t, i18n } = useTranslation();
  const navigationState =  {state : { date: dateState } }; // Current date when creating new practice

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadPractices = async () => {
      try {
        const team_id = filters?.team_ids ? filters.team_ids : teamId;
        const filter = {
          ...filters,
          team_ids: team_id,
          start_date: startDate.format('YYYY-MM-DD'),
          end_date: endDate.format('YYYY-MM-DD'),
        };
        const data = await practicesList(filter, signal);
        const groupedByDay = data?.reduce((acc, practice) => {
          const scheduled_at = dayjs(practice.scheduled_at.split('Z')[0]); // Removes 'Z' to ignore timezone
          const date = scheduled_at.format('YYYY-MM-DD');

          if (!acc[date]) acc[date] = [];
          acc[date].push({...practice, scheduled_at}); // Work with sheduled_at as a datejs date
          return acc;
        }, {});

        setDayPractices(groupedByDay);

        const today = dayjs().format('YYYY-MM-DD');
        if (groupedByDay[today]) {
          setSelectedPractices(groupedByDay[today]);
        }
      } catch (error) {
        console.error('Failed to fetch practices:', error);
      }
    };

    loadPractices();

    return () => {
      controller.abort();
    };
  }, [startDate, endDate, filters, refreshPractices]);

  useEffect(() => {
    const formattedDate = dayjs(dateState).format('YYYY-MM-DD');
    const practicesForDate = dayPractices[formattedDate] || [];

    const groupedPractices = {};

    practicesForDate.forEach(practice => {
      const hour = practice.scheduled_at.format('HH');
      groupedPractices[hour] = groupedPractices[hour] || { hour, practices: [] };
      groupedPractices[hour].practices.push(practice);
    });

    const sortedPractices = Object.values(groupedPractices).sort((a, b) => {
      return parseInt(a.hour, 10) - parseInt(b.hour, 10);
    });
  
    setSelectedPractices(sortedPractices);
  }, [dateState, dayPractices]);

  const toggleMenu = id => setMenuOpenId(prevId => (prevId === id ? null : id));

  const remove = async (practice) => {
    setPracticeToRemove({
      id: practice.id, 
      scheduled_at: practice.scheduled_at, 
      title: practice.practice_template?.title || ''
    });
  };
  
  const confirmRemove = async () => {
    const { id } = practiceToRemove;
    
    try {
      await removePractice(id);
      const updatedPractices = Object.keys(dayPractices).reduce((acc, date) => {
        acc[date] = dayPractices[date].filter(practice => practice.id !== id);
        return acc;
      }, {});
      setDayPractices(updatedPractices);
      setPracticeToRemove(null);
  
    } catch (error) {
      console.error('Failed to delete practice:', error);
      setPracticeToRemove(null);
    }
  };
  

  const activeDateChange = ({ activeStartDate }) => {
    const newStartDate = dayjs(activeStartDate).startOf('month').subtract(7, 'day');
    const newEndDate = dayjs(activeStartDate).endOf('month').add(7, 'day');

    if (!newStartDate.isSame(startDate) || !newEndDate.isSame(endDate)) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  const renderDots = date => {
    const dotCount = Math.max(0, Math.min((dayPractices[dayjs(date).format('YYYY-MM-DD')]?.length || 0) - 1, 2));
    return (
      <div className={`dots dot-${dotCount}`}>
        {Array.from({ length: dotCount }, (_, index) => (
          <div key={index} className={`dot ${dateState.toDateString() === date.toDateString() ? 'active' : ''}`} />
        ))}
      </div>
    );
  };

  const changeItem = (_, item) => {
    setPractice({ ...item });
    setIsLibraryDialogOpen(false);
    setIsDatePickerOpen(true);
  };

  const clickOutside = event => {
    if (!event.target.closest('.dropdown-toggle') && !event.target.closest('.dropdown-actions') && !event.target.closest('.dropdown_practice')) {
      setMenuOpenId(null);
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', clickOutside);
    return () => { document.removeEventListener('click', clickOutside)};
  }, []);

  const team_id = filters?.team_ids?.length === 1 ? filters.team_ids[0] : teamId;
  const isTodayOrFuture = dayjs(dateState).isSame(dayjs(), 'day') || dayjs(dateState).isAfter(dayjs(), 'day');

  const addClick = option => {
    switch (option) {
      case 'practice': { navigate(`/club_admin/teams/${team_id}/practice/new`, navigationState); break }
      case 'template': { setIsLibraryDialogOpen(true); setMethod('from_template'); break }
      case 'generate': { setIsDatePickerOpen(true); setMethod('generate'); break }
      case 'schedule': { navigate(`/club_admin/teams/${team_id}/schedule/new`); break }
    }
    setDropdownOpen(false);
  };

  return (
    <>
    <div className="form-container-area">
      <div className="calendar-editor">
        <div className="calendar-editor-scene">
          <div className="calendar-editor-scene-label">{t('teams.calendar')}</div>
          <Calendar
            value={dateState}
            onChange={setDateState}
            onActiveStartDateChange={activeDateChange}
            locale={i18n.language}
            formatDay={(_, date) => dayjs(date).format('D')}
            formatMonthYear={(_, date) => dayjs(date).format('MMM YYYY')}
            tileClassName={({ date, view }) => view === 'month' && (dayPractices[dayjs(date).format('YYYY-MM-DD')]?.length || 0) && 'has-event'}
            tileContent={({ date, view }) => view === 'month' && renderDots(date)}
          />
        </div>
        <div className="calendar-editor-action">
          <div className="practice-calendar-practices" id="practices_list">
            <div className="practice-calendar-practices-day">
              <div className="practice-calendar-practices-day-header">
                <div className="practice-calendar-practices-day-label"> {dayjs(dateState).format('dddd, D MMMM YYYY')}</div>
               
                {/* Plan Team Practices, one team selected in filters */}
                {team_id && isTodayOrFuture && (
                  <div className="dropdown">
                    <button className="button button-add dropdown-toggle" onClick={() => setDropdownOpen('team')}>
                      {t('practices.add_practice.button')}
                    </button>
                    {dropdownOpen && (
                      <div className="dropdown_practice scale-up-ver-top">
                        <div onClick={() => addClick('practice')}>{t('practices.add_practice.manual')}</div>
                        <div onClick={() => addClick('generate')}>{t('practices.add_practice.generate')}</div>
                        <div onClick={() => addClick('template')}>{t('practices.add_practice.from_template')}</div>
                        <div onClick={() => addClick('schedule')}>{t('practices.add_practice.schedule')}</div>
                      </div>
                    )}
                  </div>
                )}
                {/* Plan Club Practices */}
                { !team_id && (
                  <div className="dropdown">
                    <button className="button button-add dropdown-toggle" onClick={() => setDropdownOpen('club')}>
                      {t('practices.add_practice.button_club')}
                    </button>
                    {dropdownOpen && (
                      <div className="dropdown_practice scale-up-ver-top">
                        <div onClick={() => navigate('/club_admin/group_practices/manual', navigationState)}>{t('practices.add_practice.manual')}</div>
                        <div onClick={() => navigate('/club_admin/group_practices/generate', navigationState)}>{t('practices.add_practice.generate')}</div>
                        <div onClick={() => navigate('/club_admin/group_practices/from_template', navigationState)}>{t('practices.add_practice.from_template')}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!selectedPractices.length ? (
                <p className="practice-calendar-title">{t('teams.no_practices')}</p>
              ) : (
                selectedPractices.map(({ practices }, index) => (
                  <div key={index}>
                    {practices?.map(practice => (
                      <div key={practice.id} className="practice-calendar-practices-group">
                        <div className="practice-calendar-practices-group-time">{practice.scheduled_at.format('HH:mm')}</div>
                        <div className="practice-calendar-practices-group-items">
                          <div className="practice-calendar-practices-group-items-item">
                            <div className="practice-details">
                              <div className="practice-calendar-practices-group-items-item-team">{practice?.team_name}</div>
                              <div className="practice-calendar-practices-group-items-item-title">
                                {practice.practice_template ? (
                                  <>
                                    <strong>{practice.practice_template.title}</strong>:&nbsp;
                                    {practice.practice_template.exercises_practices
                                      .map(exercise => exercise.title)
                                      .join(', ')}
                                  </>
                                ) : (practice.exercises_practices.map(exercise => exercise.title).join(','))}
                              </div>
                            </div>
                            <div className="practice-actions dropdown">
                              <div className="dropdown-toggle" onClick={() => toggleMenu(practice.id)}><i className="icon icon-options-vertical"></i></div>
                              {menuOpenId === practice.id && (
                                <div className="dropdown-actions scale-up-ver-top">
                                  <div>
                                    <Link to={`/club_admin/teams/${practice?.team_id}/practice/${practice.id}`}><i className="icon icon-settings pointer"></i></Link>
                                  </div>
                                  {team_id && 
                                     <div onClick={() => {setIsDatePickerOpen(true); setMethod('clone'); setPractice(practice)}}>
                                      <i className="icon icon-docs pointer"></i>
                                    </div>
                                  }
                                 <div onClick={() => remove(practice)}><i className="icon icon-trash pointer"></i></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="practice-calendar-practices-separator"></div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {isLibraryDialogOpen && <LibraryDialog close={() => setIsLibraryDialogOpen(false)} onChange={changeItem} libraries={['PracticeTemplates']}/>}
  
    {isDatePickerOpen && 
    <DateDialog
      close={() => setIsDatePickerOpen(false)}
      teamId={team_id}
      practice={practice}
      method={method}
      onPracticeCreated={() => setRefreshPractices(prev => !prev)}
      initialDateTime={{ date: dateState, hour: '00', minute: '00' }}
    />
    }
    {practiceToRemove &&<ConfirmRemoveDialog   
      title={t('teams.remove_practice', { title: practiceToRemove?.title, time: dayjs(practiceToRemove?.time).format('HH:mm')})}
      confirm={confirmRemove}
      close={() => setPracticeToRemove(null)}/>}
    </>
  )
}
